import { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { HelpCircle, Upload, Download, Trash2, FileArchive, Image, Store, FileQuestion, Layers, Wand2, ShieldCheck, Dices, ChevronRight } from 'lucide-react';
import { RoundLocksPanel } from '@/components/RoundLocksPanel';
import { HistoryImport } from '@/components/HistoryImport';
import TriviaBuilderWizard from '@/components/trivia/TriviaBuilderWizard';
import SlotMachineRandomizer from '@/components/trivia/SlotMachineRandomizer';
import { MyPlans } from '@/components/trivia/MyPlans';

import ax from '@/lib/apiClient';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { key: 'overlay', label: 'Overlays', icon: Layers },
  { key: 'image', label: 'Images', icon: Image },
  { key: 'sponsor', label: 'Sponsors', icon: Store },
  { key: 'other', label: 'Other', icon: FileQuestion },
];

const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

// Round type comes from the file's name prefix (mc-05-a.bighat -> MC); anything unrecognized is SPECIAL.
const ROUND_TYPES = [
  { key: 'MC', title: 'Multiple Choice rounds' },
  { key: 'REG', title: 'General rounds' },
  { key: 'MISC', title: 'Specific rounds' },
  { key: 'MYS', title: 'Mystery rounds' },
  { key: 'BIG', title: 'BIG Question rounds' },
  { key: 'SPECIAL', title: 'Special designated rounds' },
];
const roundType = (name) => {
  const t = ((name || '').toLowerCase().match(/^([a-z]+)/) || [])[1] || '';
  if (t === 'misc') return 'MISC';
  if (t === 'mys') return 'MYS';
  if (t === 'mc') return 'MC';
  if (t === 'reg') return 'REG';
  if (t === 'big') return 'BIG';
  return 'SPECIAL';
};
const TYPE_COLORS = {
  MC: 'bg-[#5973F7]/15 text-[#8fa2ff]',
  REG: 'bg-[#22c55e]/15 text-[#4ade80]',
  MISC: 'bg-[#f97316]/15 text-[#fb923c]',
  MYS: 'bg-[#a855f7]/15 text-[#c084fc]',
  BIG: 'bg-[#fbdd68]/15 text-[#fbdd68]',
  SPECIAL: 'bg-[#ec4899]/15 text-[#f472b6]',
};

const downloadFile = async (f) => {
  try {
    const resp = await ax.get(`${API}/files/${f.file_id}/download`, { responseType: 'blob' });
    const url = URL.createObjectURL(resp.data);
    const a = document.createElement('a');
    a.href = url; a.download = f.name; a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error('Download failed'); }
};

function BighatTile({ f, canManage, onDelete }) {
  const type = f.round_type || roundType(f.name);
  return (
    <div data-testid={`file-row-${f.file_id}`}
      className="rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)] p-2.5 flex flex-col gap-1 min-w-0 panel-glow hover:-translate-y-0.5">
      <div className="flex items-center justify-between gap-1">
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold tracking-wider ${TYPE_COLORS[type]}`}>{type}</span>
        <div className="flex items-center shrink-0">
          <button data-testid={`download-file-${f.file_id}`} onClick={() => downloadFile(f)}
            className="p-1 rounded text-[#fbdd68] hover:bg-[#fbdd68]/10" title="Download"><Download className="w-3.5 h-3.5" /></button>
          {canManage && (
            <button data-testid={`delete-file-${f.file_id}`} onClick={() => onDelete(f)}
              className="p-1 rounded text-[#ef4444] hover:bg-[#ef4444]/10" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
          )}
        </div>
      </div>
      <p className="text-xs text-white font-medium truncate" title={f.name}>{f.round_name || f.name}</p>
      <p className="text-[10px] text-[#8892b0] truncate">
        {fmtSize(f.size)} · {new Date(f.created_at).toLocaleDateString()}
        {f.round_uid && (
          <span data-testid={`file-uid-${f.file_id}`} title={`Round ID ${f.round_uid}`}
            className="font-mono text-[#8fa2ff]"> · {f.round_uid.slice(0, 8)}</span>
        )}
      </p>
    </div>
  );
}

function FileRow({ f, canManage, onDelete }) {
  return (
    <div data-testid={`file-row-${f.file_id}`} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-[rgba(20,27,80,0.4)] border border-[rgba(251,221,104,0.06)]">
      <div className="min-w-0">
        <p className="text-sm text-white font-medium truncate">{f.name}</p>
        <p className="text-xs text-[#8892b0]">{fmtSize(f.size)} · {new Date(f.created_at).toLocaleDateString()} · {f.uploaded_by}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <Button data-testid={`download-file-${f.file_id}`} onClick={() => downloadFile(f)} variant="ghost" size="sm"
          className="text-[#fbdd68] hover:text-[#fbdd68] hover:bg-[#fbdd68]/10"><Download className="w-4 h-4" /></Button>
        {canManage && (
          <Button data-testid={`delete-file-${f.file_id}`} onClick={() => onDelete(f)} variant="ghost" size="sm"
            className="text-[#ef4444] hover:text-[#ef4444] hover:bg-[#ef4444]/10"><Trash2 className="w-4 h-4" /></Button>
        )}
      </div>
    </div>
  );
}

function UploadButton({ label, accept, onPick, busy, testId }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden"
        onChange={(e) => { const file = e.target.files?.[0]; if (file) onPick(file); e.target.value = ''; }} />
      <Button data-testid={testId} onClick={() => ref.current?.click()} disabled={busy}
        className="bg-[#fbdd68] text-[#000e2a] font-bold hover:bg-[#ffe685] h-9">
        <Upload className="w-4 h-4 mr-2" />{busy ? 'Uploading...' : label}
      </Button>
    </>
  );
}

export default function TriviaPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'account_owner' || user?.role === 'master_admin';
  const [data, setData] = useState({ bighat: [], venues: [], all_venues: [] });
  const [venueId, setVenueId] = useState('');
  const [category, setCategory] = useState('overlay');
  const [roundFilter, setRoundFilter] = useState('ALL');
  const [section, setSection] = useState('wizard');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [slotOpen, setSlotOpen] = useState(false);
  const [editPlan, setEditPlan] = useState(null);
  const [plansKey, setPlansKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const fetchFiles = async () => {
    try { const { data: d } = await ax.get(`${API}/files`); setData(d); if (!venueId && d.all_venues.length) setVenueId(d.all_venues[0].venue_id); }
    catch {}
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchFiles(); }, []);

  const upload = async (file, cat, vid) => {
    setBusy(true);
    const form = new FormData();
    form.append('file', file);
    form.append('category', cat);
    if (vid) form.append('venue_id', vid);
    try {
      await ax.post(`${API}/files/upload`, form);
      toast.success(`${file.name} uploaded`);
      fetchFiles();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const remove = async (f) => {
    if (!window.confirm(`Delete ${f.name}? Linked programs will drop it from their manifest.`)) return;
    try { await ax.delete(`${API}/files/${f.file_id}`); toast.success('File deleted'); fetchFiles(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const venueFiles = (data.venues.find((v) => v.venue_id === venueId)?.files || []).filter((f) => f.category === category);
  const bighatFiltered = roundFilter === 'ALL' ? data.bighat : data.bighat.filter((f) => (f.round_type || roundType(f.name)) === roundFilter);
  const typeCount = (key) => data.bighat.filter((f) => (f.round_type || roundType(f.name)) === key).length;

  return (
    <div data-testid="trivia-page">
      <div className="mb-8">
        <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#fbdd68]/80 mb-1.5">Show Content</p>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl bg-[#fbdd68]/10 border border-[#fbdd68]/20 flex items-center justify-center">
            <HelpCircle className="w-6 h-6 text-[#fbdd68]" />
          </span>
          Trivia
        </h1>
        <p className="text-sm text-[#8892b0] mt-2">Your trivia files, stored in the cloud and pushed to every linked program.</p>
      </div>

      {/* Horizontal section nav: Build Wizard | Files | Admin (owner only) */}
      <div className="flex gap-1.5 mb-6 border-b border-[rgba(251,221,104,0.1)] pb-0" data-testid="trivia-nav">
        {[{ key: 'wizard', label: 'Build Wizard', icon: Wand2 },
          { key: 'files', label: 'Files', icon: FileArchive },
          ...(canManage ? [{ key: 'admin', label: 'Admin', icon: ShieldCheck }] : [])].map((s) => (
          <button key={s.key} data-testid={`trivia-nav-${s.key}`} onClick={() => setSection(s.key)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-t-lg border-b-2 transition-colors ${section === s.key
              ? 'text-[#fbdd68] border-[#fbdd68] bg-[rgba(251,221,104,0.06)]' : 'text-[#8892b0] border-transparent hover:text-white'}`}>
            <s.icon className="w-4 h-4" />{s.label}
          </button>
        ))}
      </div>

      {section === 'wizard' && (
        <div className="space-y-6" data-testid="trivia-section-wizard">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <button data-testid="build-show-btn" onClick={() => { setEditPlan(null); setWizardOpen(true); }}
              className="group text-left rounded-xl p-5 bg-gradient-to-br from-[rgba(251,221,104,0.14)] to-[rgba(251,221,104,0.04)] border border-[rgba(251,221,104,0.35)] hover:border-[#fbdd68] hover:shadow-[0_0_24px_rgba(251,221,104,0.15)] transition-all duration-200 hover:-translate-y-0.5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-lg bg-[#fbdd68] text-[#000e2a] flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Wand2 className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold text-white flex items-center gap-1.5">
                    Build a show
                    <ChevronRight className="w-4 h-4 text-[#fbdd68] opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  </p>
                  <p className="text-xs text-[#8892b0] mt-1 leading-relaxed">
                    Step-by-step wizard: pick the host, location and rounds, then package the show for the program.
                  </p>
                </div>
              </div>
            </button>
            <button data-testid="randomizer-btn" onClick={() => setSlotOpen(true)}
              className="group text-left rounded-xl p-5 bg-gradient-to-br from-[rgba(89,115,247,0.14)] to-[rgba(89,115,247,0.04)] border border-[rgba(89,115,247,0.35)] hover:border-[#8fa2ff] hover:shadow-[0_0_24px_rgba(89,115,247,0.18)] transition-all duration-200 hover:-translate-y-0.5">
              <div className="flex items-start gap-4">
                <div className="shrink-0 w-12 h-12 rounded-lg bg-[#5973F7] text-white flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Dices className="w-6 h-6" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-base font-bold text-white flex items-center gap-1.5">
                    Round roulette
                    <ChevronRight className="w-4 h-4 text-[#8fa2ff] opacity-0 -ml-1 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                  </p>
                  <p className="text-xs text-[#8892b0] mt-1 leading-relaxed">
                    Slot machine randomizer — let the reels spin and pick tonight's rounds for you.
                  </p>
                </div>
              </div>
            </button>
          </div>
          <TriviaBuilderWizard open={wizardOpen} onOpenChange={(o) => { setWizardOpen(o); if (!o) setEditPlan(null); }}
            userName={user?.name || user?.email} initialPlan={editPlan}
            onComplete={async () => setPlansKey((k) => k + 1)} />
          <SlotMachineRandomizer open={slotOpen} onClose={() => setSlotOpen(false)}
            locations={data.all_venues.map((v) => v.venue_name)}
            onComplete={() => setPlansKey((k) => k + 1)} />
          <MyPlans refreshKey={plansKey} onEdit={(p) => { setEditPlan(p); setWizardOpen(true); }} />
        </div>
      )}

      {section === 'files' && (
      <div className="space-y-6" data-testid="trivia-section-files">
        <Card className="glass-panel border-0">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <FileArchive className="w-5 h-5 text-[#fbdd68]" />BIG Hat Files
                </CardTitle>
                <CardDescription className="text-[#8892b0] mt-1">
                  Your owned .bighat files. Every linked program receives these.
                </CardDescription>
              </div>
              {canManage && <UploadButton label="Upload .bighat" accept=".bighat" busy={busy} testId="upload-bighat-btn"
                onPick={(file) => upload(file, 'bighat', null)} />}
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1.5 flex-wrap mb-3" data-testid="round-type-filters">
              <button data-testid="round-filter-all" onClick={() => setRoundFilter('ALL')}
                className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${roundFilter === 'ALL'
                  ? 'bg-[#fbdd68] text-[#000e2a]' : 'bg-[rgba(251,221,104,0.08)] text-[#8892b0] hover:text-white'}`}>
                ALL ({data.bighat.length})
              </button>
              {ROUND_TYPES.map((t) => (
                <button key={t.key} data-testid={`round-filter-${t.key.toLowerCase()}`} onClick={() => setRoundFilter(t.key)} title={t.title}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${roundFilter === t.key
                    ? 'bg-[#fbdd68] text-[#000e2a]' : 'bg-[rgba(251,221,104,0.08)] text-[#8892b0] hover:text-white'}`}>
                  {t.key} ({typeCount(t.key)})
                </button>
              ))}
            </div>
            {bighatFiltered.length === 0 && (
              <p className="text-sm text-[#8892b0] italic" data-testid="bighat-empty">
                {data.bighat.length === 0 ? 'No .bighat files yet.' : `No ${roundFilter} rounds uploaded.`}
              </p>
            )}
            {/* 5 tiles across; the box caps at 5 rows and scrolls inside after that */}
            <div data-testid="bighat-grid"
              className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 max-h-[420px] overflow-y-auto pr-1">
              {bighatFiltered.map((f) => <BighatTile key={f.file_id} f={f} canManage={canManage} onDelete={remove} />)}
            </div>
          </CardContent>
        </Card>

        <div className="text-xs text-[#8892b0] flex items-center">
          <Badge className="bg-[#22c55e]/10 text-[#22c55e] border-[#22c55e]/30 text-[10px] mr-2">Synced</Badge>
          Linked programs fetch these files with content stamps via their manifest, so each PC knows exactly what changed.
        </div>
      </div>
      )}

      {section === 'admin' && canManage && (
      <div className="space-y-6" data-testid="trivia-section-admin">
        <Card className="glass-panel border-0">
          <CardHeader>
            <CardTitle className="text-white text-lg">Venue Trivia Assets</CardTitle>
            <CardDescription className="text-[#8892b0] mt-1">
              Overlays, images and sponsor files, grouped by location. Each PC only receives the venues assigned to it.
            </CardDescription>
            <div className="flex items-center gap-3 flex-wrap mt-3">
              <select data-testid="venue-select" value={venueId} onChange={(e) => setVenueId(e.target.value)}
                className="bg-[#000e2a] border border-[rgba(251,221,104,0.2)] text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-[#fbdd68]">
                {data.all_venues.length === 0 && <option value="">No venues yet</option>}
                {data.all_venues.map((v) => <option key={v.venue_id} value={v.venue_id}>{v.venue_name}</option>)}
              </select>
              <div className="flex gap-1.5 flex-wrap">
                {CATEGORIES.map((c) => (
                  <button key={c.key} data-testid={`category-${c.key}`} onClick={() => setCategory(c.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${category === c.key
                      ? 'bg-[#fbdd68] text-[#000e2a]' : 'bg-[rgba(251,221,104,0.08)] text-[#8892b0] hover:text-white'}`}>
                    {c.label}
                  </button>
                ))}
              </div>
              {canManage && venueId && <UploadButton label={`Upload ${category}`} accept="*" busy={busy} testId="upload-venue-file-btn"
                onPick={(file) => upload(file, category, venueId)} />}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {venueFiles.length === 0 && (
              <p className="text-sm text-[#8892b0] italic" data-testid="venue-files-empty">
                No {category} files for this venue yet.
              </p>
            )}
            {venueFiles.map((f) => <FileRow key={f.file_id} f={f} canManage={canManage} onDelete={remove} />)}
          </CardContent>
        </Card>

        <RoundLocksPanel venues={data.all_venues} canManage={canManage} />
        <HistoryImport venues={data.all_venues} />
      </div>
      )}
    </div>
  );
}
